import React, { useEffect, useState } from "react";
import { Container, Table } from "react-bootstrap";
import { getNumberOfCreatedAndResolvedDataByQuarterAPI } from "../../apiRequests/number-of-created-and-resolved/getNumberOfCreatedAndResolvedDataAPI";
import { useNavigate, useParams } from "react-router-dom";
import { APP_URLS, BLANK_STRING, VIEW_MODES } from "../../constants/appConstants";
import SwitchViewData from "../../Components/SwitchViewData/SwitchViewData";
import { exportQuarterAndYear, getQuarterString, getQuarterYearObject } from "../../utils/commonUtils";
import MultiSelector from "../../Components/MultiSelector/MultiSelector";
import { DEFAULT_QUARTER_KEY, LIST_PROJECT_KEY } from "./constants";
import MaskLoading from "../../Components/LoadingPage/MaskLoading";

interface NumberOfCreatedAndResolvedProps {
    isDashboard?: boolean;
    dataNumberOfCreatedAndResolved?: any;
}

interface DataNumberOfCreatedAndResolved {
    projectKey?: string;
    created: number;
    completed: number;
    placeHolder: number;
    total: number;
}

const NumberOfCreatedAndResolved = (props: NumberOfCreatedAndResolvedProps) => {
    const navigate = useNavigate();
    const { period } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentViewMode, setCurrentViewMode] = useState<string>(VIEW_MODES.DETAILED);
    const [selectedYear, setSelectedYear] = useState<string>(BLANK_STRING);
    const [selectedQuarter, setSelectedQuarter] = useState<string>(BLANK_STRING);
    const [selectedYearAndQuarter, setSelectedYearAndQuarter] = useState<string[]>([]);
    const [dataKPIForMultiQuarter, setDataKPIForMultiQuarter] = useState<any[]>([]);
    const [dataNumberOfCreatedAndResolved, setDataNumberOfCreatedAndResolved] = useState<any>([]);
    const [mostYearsProjectIndex, setMostYearsProjectIndex] = useState<number>(0);

    useEffect(() => {
        const getAllNumberOfCreatedAndResolvedData = async () => {
            let unloadedDataKeys: {
                projectIndex: number;
                year: string;
                quarterKey: string;
            }[] = [];

            dataNumberOfCreatedAndResolved.forEach((dataObject: any, projectIndex: number) => {
                Object.entries(dataObject[LIST_PROJECT_KEY[projectIndex]]).forEach(([year, yearData]: [string, any]) => {
                    Object.entries(yearData).forEach(([quarterKey, quarterData]: [string, any]) => {
                        if (!quarterData.isLoaded) {
                            unloadedDataKeys.push({ projectIndex, year, quarterKey });
                        }
                    });
                });
            });

            if (currentViewMode === VIEW_MODES.TABLE && unloadedDataKeys.length > 0) {
                setIsLoading(true);
                try {
                    await Promise.all(
                        unloadedDataKeys.map(async ({ projectIndex, year, quarterKey }) => {
                            const { quarter, year: yearNumber } = getQuarterYearObject(quarterKey);
                            await loadDataForOneQuarter(yearNumber, quarter, quarterKey);
                        })
                    );
                } catch (error) {
                    console.error("Error fetching Purchase Acceptance data:", error);
                }
                setIsLoading(false);
            }
        };

        getAllNumberOfCreatedAndResolvedData();
    }, [currentViewMode]);

    const setCurrentYearAndQuarter = (dataNumberOfCreatedAndResolved: any, year: number, quarter: number) => {
        if (dataNumberOfCreatedAndResolved.length) {
            setSelectedYear(year.toString());
            setSelectedQuarter(`${DEFAULT_QUARTER_KEY}${quarter},${year}`);
            const quarterString = getQuarterString(quarter);
            setSelectedYearAndQuarter([`${DEFAULT_QUARTER_KEY}${quarter},${year}`]);
            !props.isDashboard && navigate(`/${APP_URLS.NUMBER_OF_CREATED_AND_RESOLVED}/${quarterString}-${year}`, { replace: true });
        }
    };

    useEffect(() => {
        const data = selectedYearAndQuarter.map((item: any) => {
            let quarterAndYearData = {
                created: 0,
                completed: 0,
                placeHolder: 0,
                total: 0,
            };
            const { year, quarter } = getQuarterYearObject(item);

            LIST_PROJECT_KEY.forEach((projectKey: string, index: number) => {
                try {
                    const projectData = dataNumberOfCreatedAndResolved[index][projectKey][year][item];
                    quarterAndYearData.created += projectData.created;
                    quarterAndYearData.completed += projectData.completed;
                    quarterAndYearData.placeHolder += projectData.placeHolder;
                    quarterAndYearData.total += projectData.total;
                } catch (error) {}
            });
            return quarterAndYearData;
        });
        setDataKPIForMultiQuarter(data);
    }, [selectedYearAndQuarter]);

    useEffect(() => {
        !props.isDashboard && getNumberOfCreatedAndResolvedDataAndSetToState();
    }, []);

    useEffect(() => {
        if (props.isDashboard) {
            setDataNumberOfCreatedAndResolved(props.dataNumberOfCreatedAndResolved);
            const currentYear = new Date().getFullYear();
            const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
            setCurrentYearAndQuarter(props.dataNumberOfCreatedAndResolved, currentYear, currentQuarter);
        }
    }, [props.dataNumberOfCreatedAndResolved, props.isDashboard]);

    const getNumberOfCreatedAndResolvedDataAndSetToState = async () => {
        setIsLoading(true);
        const { year: periodYear, quarter: periodQuarter } = exportQuarterAndYear(period || BLANK_STRING);
        const targetYear = periodYear || new Date().getFullYear();
        const targetQuarter = periodQuarter || Math.floor(new Date().getMonth() / 3) + 1;
        const dataNumberOfCreatedAndResolved = await getNumberOfCreatedAndResolvedDataByQuarterAPI(targetYear, targetQuarter);
        if (dataNumberOfCreatedAndResolved.length > 0) {
            const mostYearsProjectIndex = dataNumberOfCreatedAndResolved.reduce((acc: number, data: any, index: number) => {
                const years = Object.keys(data[LIST_PROJECT_KEY[index]]);
                return years.length > acc ? index : acc;
            }, 0);

            setMostYearsProjectIndex(mostYearsProjectIndex - 1);
            setDataNumberOfCreatedAndResolved(dataNumberOfCreatedAndResolved);
            setCurrentYearAndQuarter(dataNumberOfCreatedAndResolved, targetYear, targetQuarter);
        }

        setIsLoading(false);
    };

    const renderNumberOfCreatedAndResolvedDataTable = () => {
        const displayData = dataNumberOfCreatedAndResolved?.map((data: any, index: number) => {
            const projectKey = LIST_PROJECT_KEY[index];
            const projectData = data?.[projectKey]?.[selectedYear]?.[selectedQuarter];

            if (projectData) {
                return {
                    projectKey,
                    ...projectData,
                };
            }

            return {
                projectKey,
                created: 0,
                completed: 0,
                placeHolder: 0,
                total: 0,
            };
        });
        return (
            <div>
                <h1>Number of Created and Resolved KPI</h1>
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>JIRA Project Code</th>
                            <th>Number of Issues Completed</th>
                            <th>Number of Issues Not Completed</th>
                            <th>Total Number of Issues</th>
                            <th>% completion</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayData.map((data: DataNumberOfCreatedAndResolved, index: number) => {
                            return (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{data.projectKey}</td>
                                    <td>{data.completed}</td>
                                    <td>{data.created}</td>
                                    <td>{data.total - data.placeHolder}</td>
                                    <td>{((data.completed / (data.total - data.placeHolder)) * 100 || 0).toFixed(2)}%</td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td>{dataNumberOfCreatedAndResolved.length + 1}</td>
                            <td>
                                <b>Total</b>
                            </td>
                            <td>
                                <b>{renderTotalCompletedIssues(displayData)}</b>
                            </td>
                            <td>
                                <b>{renderTotalNotCompletedIssues(displayData)}</b>
                            </td>
                            <td>
                                <b>{renderTotalIssues(displayData)}</b>
                            </td>
                            <td>
                                <b>{renderTotalKpiCompletion(displayData)}</b>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
        );
    };

    const renderTotalCompletedIssues = (displayData: any) => {
        return (
            displayData.reduce((acc: number, data: DataNumberOfCreatedAndResolved) => {
                return acc + data.completed;
            }, 0) || 0
        );
    };

    const renderTotalNotCompletedIssues = (displayData: any) => {
        return (
            displayData.reduce((acc: number, data: DataNumberOfCreatedAndResolved) => {
                return acc + data.created;
            }, 0) || 0
        );
    };

    const renderTotalIssues = (displayData: any) => {
        return (
            displayData.reduce((acc: number, data: DataNumberOfCreatedAndResolved) => {
                return acc + data.total - data.placeHolder;
            }, 0) || 0
        );
    };

    const renderTotalKpiCompletion = (displayData: any) => {
        return ((renderTotalCompletedIssues(displayData) / renderTotalIssues(displayData)) * 100 || 0).toFixed(2) + "%";
    };

    const renderLoading = () => {
        return <MaskLoading></MaskLoading>;
    };

    const renderSelectYear = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="year">
                    Select Year:
                </label>
                <select
                    className="select"
                    id="year"
                    value={selectedYear}
                    onChange={(event) => {
                        setSelectedYear(event.target.value);
                    }}
                >
                    <option disabled value="">
                        Select Year
                    </option>
                    {dataNumberOfCreatedAndResolved.length > 0 &&
                        Object.keys(dataNumberOfCreatedAndResolved[mostYearsProjectIndex][LIST_PROJECT_KEY[mostYearsProjectIndex]]).map(
                            (data: any) => (
                                <option key={data} value={data}>
                                    {data}
                                </option>
                            )
                        )}
                </select>
            </div>
        );
    };

    const handleQuarterChange = async (event: any) => {
        const quarterKey = event.target.value;
        setSelectedQuarter(quarterKey);
        const { quarter, year } = getQuarterYearObject(quarterKey);
        if (!dataNumberOfCreatedAndResolved[0][LIST_PROJECT_KEY[0]][year][quarterKey].isLoaded) {
            loadDataForOneQuarter(year, quarter, quarterKey);
        }
        const quarterInString = getQuarterString(quarter);
        !props.isDashboard && navigate(`/${APP_URLS.NUMBER_OF_CREATED_AND_RESOLVED}/${quarterInString}-${year}`, { replace: true });
    };

    const loadDataForOneQuarter = async (year: number, quarter: number, quarterKey: string) => {
        setIsLoading(true);
        const data = await getNumberOfCreatedAndResolvedDataByQuarterAPI(year, quarter);
        setDataNumberOfCreatedAndResolved((prevState: any) => {
            const newState = [...prevState].map((item: any, index: number) => {
                item[LIST_PROJECT_KEY[index]][year][quarterKey] = data[index][LIST_PROJECT_KEY[index]][year][quarterKey];
                item[LIST_PROJECT_KEY[index]][year][quarterKey].isLoaded = true;
                return item;
            });
            return newState;
        });
        setIsLoading(false);
    };
    const renderSelectQuarter = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="quarter">
                    Select Quarter:
                </label>
                <select
                    className="select"
                    id="quarter"
                    value={selectedQuarter}
                    onChange={(event) => {
                        handleQuarterChange(event);
                    }}
                >
                    <option value="">Select Quarter</option>
                    {dataNumberOfCreatedAndResolved.length > 0 &&
                        selectedYear &&
                        Object.keys(dataNumberOfCreatedAndResolved[mostYearsProjectIndex][LIST_PROJECT_KEY[mostYearsProjectIndex]][selectedYear]).map(
                            (data: any) => {
                                return <option key={data}>{data}</option>;
                            }
                        )}
                </select>
            </div>
        );
    };

    const renderDetailedView = () => {
        return (
            <>
                {renderSelectYear()}
                {renderSelectQuarter()}
                {selectedQuarter && renderNumberOfCreatedAndResolvedDataTable()}
            </>
        );
    };

    const renderTableMultiQuarter = () => {
        return (
            <Table>
                <thead>
                    <tr>
                        <th></th>
                        <th>Number of Issues Completed</th>
                        <th>Number of Issues Not Completed</th>
                        <th>Total Number of Issues</th>
                        <th>% completion</th>
                    </tr>
                </thead>
                <tbody>
                    {dataKPIForMultiQuarter.map((data: any, index: number) => (
                        <tr key={selectedYearAndQuarter[index]}>
                            <td>{selectedYearAndQuarter[index]}</td>
                            <td>{data.completed}</td>
                            <td>{data.created}</td>
                            <td>{data.total - data.placeHolder}</td>
                            <td>{((data.completed / (data.total - data.placeHolder)) * 100 || 0).toFixed(2)}%</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    const sortStringQuarterAndYear = (a: string, b: string): number => {
        const [quarterA, yearA] = a.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));
        const [quarterB, yearB] = b.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));

        if (yearA > yearB) {
            return -1;
        } else if (yearA < yearB) {
            return 1;
        } else {
            return parseInt(quarterB) - parseInt(quarterA);
        }
    };

    function extractAllQuarterKeys() {
        const data = dataNumberOfCreatedAndResolved[mostYearsProjectIndex][LIST_PROJECT_KEY[mostYearsProjectIndex]];

        const allQuarterKeys = Object.keys(data).reduce((acc: any[], year: string) => {
            if (year) {
                const quarterKeys = Object.keys(data[year]);
                return [...acc, ...quarterKeys];
            }
            return acc;
        }, []);

        return allQuarterKeys.sort(sortStringQuarterAndYear);
    }

    const renderTableViewMultiQuarter = () => {
        return (
            <>
                <MultiSelector
                    dataOptions={extractAllQuarterKeys()}
                    currentSelected={selectedYearAndQuarter.sort(sortStringQuarterAndYear)}
                    onChange={(data: any[]) => {
                        setSelectedYearAndQuarter(data.sort(sortStringQuarterAndYear));
                    }}
                    title="Select Year And Quarter"
                ></MultiSelector>
                {selectedYearAndQuarter.length > 0 && renderTableMultiQuarter()}
            </>
        );
    };

    const renderMainContentOfNumberOfCreatedAndResolved = () => {
        return (
            <div>
                {isLoading && renderLoading()}
                <SwitchViewData currentViewMode={currentViewMode} setCurrentViewMode={(view) => setCurrentViewMode(view)}></SwitchViewData>
                {currentViewMode === VIEW_MODES.DETAILED && renderDetailedView()}
                {currentViewMode === VIEW_MODES.TABLE && renderTableViewMultiQuarter()}
            </div>
        );
    };

    return <Container className="mt-5">{renderMainContentOfNumberOfCreatedAndResolved()}</Container>;
};

export default NumberOfCreatedAndResolved;
